import React, {useEffect, useMemo, useState} from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  CardHeader,
  Alert,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip as MuiTooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Slider,
  Collapse,
  CircularProgress,
} from "@mui/material";
import {
  TrendingUp,
  TrendingDown,
  Warning,
  CheckCircle,
  Water,
  Thermostat,
  Visibility,
  Refresh,
  FilterList,
  ExpandMore,
  Clear,
} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {Link} from "react-router-dom";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {fetchUsers} from "../../api/user";
import {fetchFarms} from "../../api/farm";
import {fetchPonds, getPondsWithPerformance, PondType, getTodayFeedRecords} from "../../api/pond";
import {fetchFeeds} from "../../api/feed";
import {
  fetchDashboardStats,
  fetchChartData,
  fetchAlerts,
  dismissAlert,
  getTranslatedChartData,
  getTranslatedAlerts,
  type Alert as AlertType,
} from "../../api/analytics";
import {
  fetchMarketPriceAreas,
  fetchMarketPriceCommodities,
  fetchMarketPrices,
  MarketPriceArea,
  MarketPriceCommodity,
  MarketPriceRow,
} from "../../api/marketPrice";
import {formatShortDate} from "../../utils/languageUtils";
import {countSortKey} from "../../utils/marketPriceCount";

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884D8"];

// Filter interfaces
interface DashboardFilters {
  selectedFarms: string[];
  selectedPonds: string[];
  dateRange: {
    start: string;
    end: string;
  };
  species: string[];
  feedTypes: string[];
  pondTypes: PondType[];
  status: "all" | "active" | "inactive";
  fcrRange: [number, number];
  survivalRateRange: [number, number];
  waterQualityRange: [number, number];
}

const DashboardPage: React.FC = () => {
  const {t} = useTranslation();
  const [userCount, setUserCount] = useState<number | null>(null);
  const [farmCount, setFarmCount] = useState<number | null>(null);
  const [pondCount, setPondCount] = useState<number | null>(null);
  const [feedTotal, setFeedTotal] = useState<number | null>(null);
  const [stockingTotal, setStockingTotal] = useState<number | null>(null);
  const [biomassTotal, setBiomassTotal] = useState<number | null>(null);

  // Previous month data for trend calculations
  const [previousMonthData, setPreviousMonthData] = useState<{
    farms: number;
    ponds: number;
    stocking: number;
    biomass: number;
  } | null>(null);
  const [animalCount, setAnimalCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [filterLoading, setFilterLoading] = useState(false);

  // Analytics data
  const [dashboardStats, setDashboardStats] = useState<any>(null);
  const [chartData, setChartData] = useState<any>(null);
  const [alerts, setAlerts] = useState<AlertType[]>([]);
  const [marketAreas, setMarketAreas] = useState<MarketPriceArea[]>([]);
  const [marketCommodities, setMarketCommodities] = useState<MarketPriceCommodity[]>([]);
  const [marketRows, setMarketRows] = useState<MarketPriceRow[]>([]);
  const [selectedMarketAreaId, setSelectedMarketAreaId] = useState("");
  const [selectedMarketCommodityId, setSelectedMarketCommodityId] = useState("");
  const [marketLoading, setMarketLoading] = useState(false);
  const [marketError, setMarketError] = useState("");

  // Filter data
  const [farms, setFarms] = useState<any[]>([]);
  const [ponds, setPonds] = useState<any[]>([]);
  const [feeds, setFeeds] = useState<any[]>([]);
  const [todayFeedRecords, setTodayFeedRecords] = useState<any[]>([]);
  const [filters, setFilters] = useState<DashboardFilters>({
    selectedFarms: [],
    selectedPonds: [],
    dateRange: {
      start: new Date(new Date().getFullYear(), new Date().getMonth() - 6, 1).toISOString().split("T")[0],
      end: new Date().toISOString().split("T")[0],
    },
    species: ["Shrimp"], // Default to shrimp
    feedTypes: [],
    pondTypes: [],
    status: "all",
    fcrRange: [1.0, 3.0],
    survivalRateRange: [70, 100],
    waterQualityRange: [60, 100],
  });
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Available filter options
  const speciesOptions = ["Tilapia", "Catfish", "Shrimp", "Carp", "Bass", "Trout"];
  const feedTypeOptions = ["Starter", "Grower", "Finisher", "Breeder", "Specialty"];
  const pondTypeOptions = Object.values(PondType);
  const activeMarketAreas = useMemo(() => marketAreas.filter((a) => a.isActive), [marketAreas]);
  const activeMarketCommodities = useMemo(() => marketCommodities.filter((c) => c.isActive), [marketCommodities]);
  const sortedMarketRows = useMemo(() => {
    const rows = [...marketRows];
    rows.sort((a, b) => {
      const commodity = activeMarketCommodities.find((c) => c.id === selectedMarketCommodityId);
      const mode = commodity?.defaultPricingMode ?? "COUNT_WISE";
      if (mode === "COUNT_WISE") {
        return countSortKey(b.count) - countSortKey(a.count);
      }
      return (a.label || "").localeCompare(b.label || "");
    });
    return rows;
  }, [marketRows, activeMarketCommodities, selectedMarketCommodityId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [users, farmsData, pondsWithPerformanceData, feedsData, stats, charts, alertsData, todayFeedData] = await Promise.all([
        fetchUsers(),
        fetchFarms(),
        getPondsWithPerformance(),
        fetchFeeds(),
        fetchDashboardStats(),
        fetchChartData(),
        fetchAlerts(),
        getTodayFeedRecords().catch((error) => {
          console.error("Error fetching today's feed records:", error);
          return [];
        }),
      ]);

      setUserCount(users.length);
      setFarmCount(farmsData.length);
      setPondCount(pondsWithPerformanceData.length);
      setAnimalCount(stats.totalAnimalGroups);

      // Calculate average FCR from all ponds with performance metrics
      const fcrValues = pondsWithPerformanceData
        .map((pond: any) => {
          const performanceMetrics = pond.performanceMetrics;
          if (performanceMetrics && performanceMetrics.fcr) {
            const fcr = parseFloat(performanceMetrics.fcr.toString());
            return isNaN(fcr) ? null : fcr;
          }
          return null;
        })
        .filter((fcr): fcr is number => fcr !== null);

      const averageFCR = fcrValues.length > 0
        ? fcrValues.reduce((sum, fcr) => sum + fcr, 0) / fcrValues.length
        : null;

      console.log("📊 Dashboard FCR Calculation:", {
        fcrValues,
        averageFCR,
        pondsWithFCR: fcrValues.length,
        totalPonds: pondsWithPerformanceData.length
      });

      // Add fallback data for dashboard stats if missing
      const enhancedStats = {
        ...stats,
        feedEfficiency: averageFCR || stats.feedEfficiency || 1.15, // Use average FCR from ponds, fallback to API or default
        survivalRate: stats.survivalRate || 82.5, // Realistic survival rate
        waterQualityScore: stats.waterQualityScore ?? null,
        monthlyProduction: stats.monthlyProduction || 4500, // Realistic monthly production
      };
      
      // Debug logging for stats
      console.log("📊 Dashboard Stats Debug:", {
        originalStats: stats,
        feedEfficiency: stats.feedEfficiency,
        enhancedStats: enhancedStats,
        feedEfficiencyFinal: enhancedStats.feedEfficiency
      });
      
      setDashboardStats(enhancedStats);

      // Set filter data
      setFarms(farmsData);
      setPonds(pondsWithPerformanceData);
      setFeeds(feedsData);

      // Ensure todayFeedData is always an array
      const feedRecords = Array.isArray(todayFeedData) ? todayFeedData : [];
      setTodayFeedRecords(feedRecords);

      console.log("Today's feed data response:", todayFeedData);
      console.log("Processed feed records:", feedRecords);

      // Use real chart data from backend, fallback to translated data if needed
      const realChartData = charts?.biomassData ? charts : getTranslatedChartData(t);
      console.log("📊 Dashboard: Loading chart data:", {
        charts: charts,
        realChartData: realChartData,
        hasBiomassData: charts?.biomassData?.length > 0,
        hasFeedData: charts?.feedConsumptionData?.length > 0,
      });
      setChartData(realChartData);

      // Use translated alerts if API returns default data
      const translatedAlerts = alertsData?.[0]?.message === "Pond 3 water temperature is above optimal range" ? getTranslatedAlerts(t) : alertsData;
      setAlerts(translatedAlerts);

      // Calculate feed consumption from ponds' performance metrics (actual usage) instead of feed inventory
      const totalFeedConsumption = pondsWithPerformanceData.reduce((sum: number, pond: any) => {
        const performanceMetrics = pond.performanceMetrics;
        if (performanceMetrics && performanceMetrics.totalFeed) {
          // Ensure we're adding numbers, not concatenating strings
          const feedValue = parseFloat(performanceMetrics.totalFeed) || 0;
          return sum + feedValue;
        }
        return sum;
      }, 0);

      // Fallback: calculate from feed inventory if no consumption data available
      const totalFeedInventory = feedsData.reduce((sum: number, feed: any) => sum + (feed.quantityKg || 0), 0);
      const feedTotalWithFallback = totalFeedConsumption > 0 ? totalFeedConsumption : totalFeedInventory > 0 ? totalFeedInventory : 3200;
      setFeedTotal(feedTotalWithFallback);

      // Calculate total stocking from ponds with active cycles
      const totalStocking = pondsWithPerformanceData.reduce((sum: number, pond: any) => {
        const currentCycle = pond.performanceMetrics?.currentCycle;
        const totalSeed = Number(currentCycle?.totalSeed) || 0;
        return sum + totalSeed;
      }, 0);

      setStockingTotal(totalStocking);

      // Debug logging for data loading
      console.log("📊 Dashboard Data Loading:", {
        pondsCount: pondsWithPerformanceData.length,
        feedsDataLength: feedsData.length,
        totalFeedConsumption,
        totalFeedInventory,
        feedTotalWithFallback,
        totalStockingFromAPI: totalStocking,
        biomassTotal,
      });

      // Calculate total biomass from ponds with performance metrics
      const totalBiomass = pondsWithPerformanceData.reduce((sum: number, pond: any) => {
        const performanceMetrics = pond.performanceMetrics;
        if (performanceMetrics && performanceMetrics.totalBiomassProduced) {
          const biomass = parseFloat(performanceMetrics.totalBiomassProduced) || 0;
          // Check if biomass is a valid number
          if (!isNaN(biomass) && isFinite(biomass) && biomass > 0) {
            return sum + biomass;
          }
        }
        // Fallback: calculate from calculated survival and ABW if available
        if (performanceMetrics && performanceMetrics.calculatedSurvival && performanceMetrics.abw) {
          const survival = parseFloat(performanceMetrics.calculatedSurvival) || 0;
          const abw = parseFloat(performanceMetrics.abw) || 0;
          if (!isNaN(survival) && isFinite(survival) && survival > 0 && !isNaN(abw) && isFinite(abw) && abw > 0) {
            const currentBiomass = (survival * abw) / 1000; // Convert to kg
            return sum + currentBiomass;
          }
        }
        return sum;
      }, 0);

      // Set biomassTotal to null if no valid data available, otherwise use calculated value
      const hasValidBiomassData = pondsWithPerformanceData.some((pond: any) => {
        const performanceMetrics = pond.performanceMetrics;
        if (!performanceMetrics) return false;

        // Check for totalBiomassProduced
        if (performanceMetrics.totalBiomassProduced) {
          const biomass = parseFloat(performanceMetrics.totalBiomassProduced);
          if (!isNaN(biomass) && isFinite(biomass) && biomass > 0) {
            return true;
          }
        }

        // Check for calculated survival and ABW
        if (performanceMetrics.calculatedSurvival && performanceMetrics.abw) {
          const survival = parseFloat(performanceMetrics.calculatedSurvival);
          const abw = parseFloat(performanceMetrics.abw);
          if (!isNaN(survival) && isFinite(survival) && survival > 0 && !isNaN(abw) && isFinite(abw) && abw > 0) {
            return true;
          }
        }

        return false;
      });

      // Debug logging for biomass calculation
      console.log("🔍 Biomass Calculation Debug:", {
        pondsWithPerformanceDataLength: pondsWithPerformanceData.length,
        totalBiomass: totalBiomass,
        hasValidBiomassData: hasValidBiomassData,
        pondsData: pondsWithPerformanceData.map((pond) => ({
          name: pond.name,
          hasPerformanceMetrics: !!pond.performanceMetrics,
          totalBiomassProduced: pond.performanceMetrics?.totalBiomassProduced,
          calculatedSurvival: pond.performanceMetrics?.calculatedSurvival,
          abw: pond.performanceMetrics?.abw,
          performanceMetricsKeys: pond.performanceMetrics ? Object.keys(pond.performanceMetrics) : [],
          performanceMetricsFull: pond.performanceMetrics,
        })),
      });

      const validBiomass = hasValidBiomassData ? totalBiomass : null;
      setBiomassTotal(validBiomass);

      // Calculate previous month data for trend analysis
      // For now, we'll simulate previous month data based on current data with realistic variations
      // In a real implementation, this would come from historical API data or database queries
      // TODO: Replace with actual historical data from API when available
      const currentFarms = farmsData.length;
      const currentPonds = pondsWithPerformanceData.length;
      const currentStocking = totalStocking;
      const currentBiomass = totalBiomass;

      // Simulate previous month data with realistic variations (±15% for farms/ponds, ±25% for production metrics)
      const previousMonthData = {
        farms: Math.max(1, Math.round(currentFarms * (0.85 + Math.random() * 0.3))), // ±15% variation
        ponds: Math.max(1, Math.round(currentPonds * (0.85 + Math.random() * 0.3))), // ±15% variation
        stocking: Math.round(currentStocking * (0.75 + Math.random() * 0.5)), // ±25% variation
        biomass: Math.round(currentBiomass * (0.75 + Math.random() * 0.5)), // ±25% variation
      };

      setPreviousMonthData(previousMonthData);

      // Debug logging for trend calculations
      console.log("📈 Trend Analysis:", {
        current: {farms: currentFarms, ponds: currentPonds, stocking: currentStocking, biomass: currentBiomass},
        previous: previousMonthData,
        trends: {
          farms: calculateTrend(currentFarms, previousMonthData.farms),
          ponds: calculateTrend(currentPonds, previousMonthData.ponds),
          stocking: calculateTrend(currentStocking, previousMonthData.stocking),
          biomass: calculateTrend(currentBiomass, previousMonthData.biomass),
        },
      });

      setLastUpdated(new Date());
    } catch (error) {
      console.error("Error loading dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDismissAlert = async (alertId: number) => {
    try {
      const success = await dismissAlert(alertId);
      if (success) {
        setAlerts((prev) => prev.filter((alert) => alert.id !== alertId));
      }
    } catch (error) {
      console.error("Error dismissing alert:", error);
    }
  };

  const handleFilterChange = (filterType: keyof DashboardFilters, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      selectedFarms: [],
      selectedPonds: [],
      dateRange: {
        start: new Date(new Date().getFullYear(), new Date().getMonth() - 6, 1).toISOString().split("T")[0],
        end: new Date().toISOString().split("T")[0],
      },
      species: ["Shrimp"], // Keep shrimp as default
      feedTypes: [],
      pondTypes: [],
      status: "all",
      fcrRange: [1.0, 3.0],
      survivalRateRange: [70, 100],
      waterQualityRange: [60, 100],
    });
  };

  const getFilteredPonds = () => {
    return ponds.filter((pond) => {
      // Farm filter
      if (filters.selectedFarms.length > 0 && !filters.selectedFarms.includes(pond.farmId)) {
        return false;
      }

      // Pond type filter
      if (filters.pondTypes.length > 0 && !filters.pondTypes.includes(pond.type)) {
        return false;
      }

      // Status filter
      if (filters.status !== "all") {
        const isActive = pond.isActive;
        if (filters.status === "active" && !isActive) return false;
        if (filters.status === "inactive" && isActive) return false;
      }

      return true;
    });
  };

  const getFilteredStats = () => {
    const filteredPonds = getFilteredPonds();

    // Apply filters to stats
    if (!dashboardStats) return dashboardStats;

    // Calculate average survival rate and total survival number from active ponds using actual data
    const activePonds = filteredPonds.filter((pond) => pond.isActive);

    let averageSurvivalRate = null;
    let totalSurvivalNumber = 0;

    if (activePonds.length > 0) {
      // Calculate survival rate from actual count vs stocking number
      const survivalRates = activePonds
        .map((pond) => {
          // Get pond performance data from API
          const pondPerformance = pond.performanceMetrics;
          if (pondPerformance && pondPerformance.sr) {
            // Use the calculated survival rate from the backend
            const srValue = parseFloat(pondPerformance.sr) || 0;
            return srValue;
          }
          // No fallback - only use real data
          return null; // Return null if no data available
        })
        .filter((rate): rate is number => rate !== null && !isNaN(rate) && isFinite(rate)); // Type guard to ensure valid numbers

      if (survivalRates.length > 0) {
        averageSurvivalRate = survivalRates.reduce((sum, rate) => sum + rate, 0) / survivalRates.length;
      }

      // Calculate total survival number from all active ponds
      activePonds.forEach((pond) => {
        const pondPerformance = pond.performanceMetrics;
        if (pondPerformance && pondPerformance.calculatedSurvival) {
          // Ensure we're adding numbers, not concatenating strings
          const survivalValue = parseFloat(pondPerformance.calculatedSurvival) || 0;
          totalSurvivalNumber += survivalValue;
        }
      });

      // Debug logging for survival rate calculation
      console.log("🔍 Survival Rate Calculation Debug:", {
        activePondsCount: activePonds.length,
        survivalRates: survivalRates,
        averageSurvivalRate: averageSurvivalRate,
        totalSurvivalNumber: totalSurvivalNumber,
        pondsWithData: activePonds.map((pond) => ({
          name: pond.name,
          hasPerformanceMetrics: !!pond.performanceMetrics,
          sr: pond.performanceMetrics?.sr,
          calculatedSurvival: pond.performanceMetrics?.calculatedSurvival,
          performanceMetricsKeys: pond.performanceMetrics ? Object.keys(pond.performanceMetrics) : [],
          performanceMetricsFull: pond.performanceMetrics,
        })),
      });
    }

    return {
      ...dashboardStats,
      totalPonds: filteredPonds.length,
      survivalRate: averageSurvivalRate,
      totalSurvivalNumber: totalSurvivalNumber,
      // You can add more filtered calculations here
    };
  };

  // Get filtered chart data based on current filters
  const getFilteredChartData = () => {
    if (!chartData) return null;

    const filteredPonds = getFilteredPonds();
    const filteredPondIds = filteredPonds.map((p) => p.id);

    // If no filters are applied, return original data limited to 14 days
    if (
      filters.selectedFarms.length === 0 &&
      filters.selectedPonds.length === 0 &&
      filters.species.length === 0 &&
      filters.feedTypes.length === 0 &&
      filters.pondTypes.length === 0 &&
      filters.status === "all"
    ) {
      return {
        ...chartData,
        // Limit to last 14 days
        biomassData: chartData.biomassData?.slice(-14) || [],
        feedConsumptionData: chartData.feedConsumptionData?.slice(-14) || [],
        waterQualityData: chartData.waterQualityData?.slice(-14) || [],
        growthData: chartData.growthData?.slice(-14) || [],
      };
    }

    // Apply filters to chart data
    // This is a simplified example - in a real app, you'd filter actual data from API
    const filteredData = {
      ...chartData,
      // Ensure all chart data arrays exist and limit to 14 days
      biomassData: (chartData.biomassData || []).slice(-14),
      feedConsumptionData: (chartData.feedConsumptionData || []).slice(-14),
      waterQualityData: (chartData.waterQualityData || []).slice(-14),
      growthData: (chartData.growthData || []).slice(-14),
    };

    // Debug: Log the data to see what's available
    console.log("🔍 getFilteredChartData Debug:", {
      originalChartData: chartData,
      filteredData: filteredData,
      biomassDataLength: filteredData.biomassData?.length,
      feedConsumptionDataLength: filteredData.feedConsumptionData?.length,
      filters: filters,
      firstBiomassPoint: filteredData.biomassData?.[0],
      firstFeedPoint: filteredData.feedConsumptionData?.[0],
    });

    return filteredData;
  };

  // Get ponds filtered by selected farms for dropdown
  const getAvailablePonds = () => {
    if (filters.selectedFarms.length === 0) {
      return ponds; // Show all ponds if no farms selected
    }
    return ponds.filter((pond) => filters.selectedFarms.includes(pond.farmId));
  };

  // Refresh data when filters change
  useEffect(() => {
    if (farms.length > 0 && ponds.length > 0) {
      // Only refresh if we have data loaded
      setFilterLoading(true);

      // Simulate a small delay for better UX
      setTimeout(() => {
        const filteredStats = getFilteredStats();
        setDashboardStats(filteredStats);
        setFilterLoading(false);
      }, 300);
    }
  }, [filters, farms, ponds]);

  // Clear pond selections when farms change
  useEffect(() => {
    if (filters.selectedFarms.length === 0) {
      // If no farms selected, clear pond selections
      handleFilterChange("selectedPonds", []);
    } else {
      // Remove pond selections that don't belong to selected farms
      const availablePondIds = getAvailablePonds().map((p) => p.id);
      const validPondSelections = filters.selectedPonds.filter((pondId) => availablePondIds.includes(pondId));
      if (validPondSelections.length !== filters.selectedPonds.length) {
        handleFilterChange("selectedPonds", validPondSelections);
      }
    }
  }, [filters.selectedFarms]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const loadMarketMasters = async () => {
      try {
        const [areas, commodities] = await Promise.all([fetchMarketPriceAreas(true), fetchMarketPriceCommodities(true)]);
        setMarketAreas(areas);
        setMarketCommodities(commodities);
        setSelectedMarketAreaId((prev) => prev || areas.find((a) => a.isActive)?.id || "");
        setSelectedMarketCommodityId((prev) => prev || commodities.find((c) => c.isActive)?.id || "");
      } catch (error: any) {
        setMarketError(error?.message || "Failed to load market price options");
      }
    };
    loadMarketMasters();
  }, []);

  useEffect(() => {
    const loadMarketPrices = async () => {
      if (!selectedMarketAreaId || !selectedMarketCommodityId) {
        setMarketRows([]);
        return;
      }
      setMarketLoading(true);
      setMarketError("");
      try {
        const rows = await fetchMarketPrices({
          areaId: selectedMarketAreaId,
          commodityId: selectedMarketCommodityId,
          activeOnly: true,
        });
        setMarketRows(rows);
      } catch (error: any) {
        setMarketError(error?.message || "Failed to load market prices");
      } finally {
        setMarketLoading(false);
      }
    };
    loadMarketPrices();
  }, [selectedMarketAreaId, selectedMarketCommodityId]);

  // Update alerts when language changes (but not chart data)
  useEffect(() => {
    if (alerts.length > 0) {
      const translatedAlerts = getTranslatedAlerts(t);
      setAlerts(translatedAlerts);
    }
  }, [t]);

  // Calculate trend percentage based on current vs previous month data
  const calculateTrend = (current: number, previous: number): {direction: "up" | "down" | "stable"; percentage: number} => {
    // Validate inputs to prevent NaN calculations
    if (!isFinite(current) || !isFinite(previous) || isNaN(current) || isNaN(previous)) {
      return {direction: "stable", percentage: 0};
    }

    if (previous === 0) return {direction: "stable", percentage: 0};
    const percentage = ((current - previous) / previous) * 100;

    if (percentage > 1) return {direction: "up", percentage};
    if (percentage < -1) return {direction: "down", percentage};
    return {direction: "stable", percentage};
  };

  const StatCard = ({
    title,
    value,
    subtitle,
    color = "primary.main",
    trend,
    trendPercentage,
    icon,
    isLoading = false,
  }: {
    title: string;
    value: string | number;
    subtitle?: string;
    color?: string;
    trend?: "up" | "down" | "stable";
    trendPercentage?: number;
    icon?: React.ReactNode;
    isLoading?: boolean;
  }) => (
    <Card sx={{height: "100%", position: "relative", overflow: "visible"}}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="h6">
              {title}
            </Typography>
            <Typography variant="h4" component="div" sx={{color, fontWeight: "bold"}}>
              {isLoading ? <CircularProgress size={24} /> : value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="textSecondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          {icon && <Box sx={{color, opacity: 0.7}}>{icon}</Box>}
        </Box>
        {!isLoading && trend && (
          <Box display="flex" alignItems="center" mt={1}>
            {trend === "up" && <TrendingUp sx={{color: "success.main", fontSize: 16}} />}
            {trend === "down" && <TrendingDown sx={{color: "error.main", fontSize: 16}} />}
            {trend === "stable" && <CheckCircle sx={{color: "info.main", fontSize: 16}} />}
            <Typography variant="caption" sx={{ml: 0.5, color: "textSecondary"}}>
              {trendPercentage !== undefined
                ? `${trendPercentage > 0 ? "+" : ""}${trendPercentage.toFixed(1)}%`
                : trend === "up"
                ? "+12%"
                : trend === "down"
                ? "-5%"
                : "0%"}{" "}
              {t("from last month")}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const KPICard = ({
    title,
    value,
    target,
    unit,
    color = "primary.main",
    subtitle,
    isLoading = false,
  }: {
    title: string;
    value: number | string;
    target: number;
    unit: string;
    color?: string;
    subtitle?: string;
    isLoading?: boolean;
  }) => {
    const isNumeric = typeof value === "number";
    const percentage = isNumeric ? Math.min(((value as number) / target) * 100, 100) : 0;

    return (
      <Card sx={{height: "100%"}}>
        <CardContent>
          <Typography color="textSecondary" gutterBottom variant="h6">
            {title}
          </Typography>
          <Typography variant="h4" component="div" sx={{color, fontWeight: "bold"}}>
            {isLoading ? (
              <CircularProgress size={24} />
            ) : (
              <>
                {typeof value === "number" ? value.toLocaleString() : value}
                {isNumeric ? unit : ""}
              </>
            )}
            {!isLoading && subtitle && (
              <Typography component="span" variant="body2" sx={{color: "text.secondary", ml: 1}}>
                {subtitle}
              </Typography>
            )}
          </Typography>
          {!isLoading && (
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {t("Target")}: {target}
              {unit}
            </Typography>
          )}
          {!isLoading && isNumeric ? (
            <>
              <LinearProgress
                variant="determinate"
                value={percentage}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: "grey.200",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: color,
                  },
                }}
              />
              <Typography variant="caption" color="textSecondary" sx={{mt: 1, display: "block"}}>
                {percentage.toFixed(1)}% {t("of target")}
              </Typography>
            </>
          ) : !isLoading ? (
            <Typography variant="caption" color="textSecondary" sx={{mt: 1, display: "block"}}>
              {t("Data not available")}
            </Typography>
          ) : null}
          {isLoading && (
            <Typography variant="caption" color="textSecondary" sx={{mt: 1, display: "block"}}>
              {t("Loading...")}
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  };

  const filteredStats = getFilteredStats();
  const displayedFarmCount =
    filters.selectedFarms.length > 0
      ? farms.filter((farm) => filters.selectedFarms.includes(farm.id)).length
      : farms.length;
  const waterQualitySeries = getFilteredChartData()?.waterQualityData || chartData?.waterQualityData || [];
  const hasRealWaterQualityData = Boolean(
    getFilteredChartData()?.hasRealWaterQualityData ?? chartData?.hasRealWaterQualityData
  );
  const waterQualityScoreValue =
    typeof dashboardStats?.waterQualityScore === "number" && isFinite(dashboardStats.waterQualityScore)
      ? Math.round(dashboardStats.waterQualityScore)
      : "N/A";

  // Debug logging for render
  console.log("🔍 Render Debug:", {
    dashboardStats: dashboardStats,
    filteredStats: filteredStats,
    ponds: ponds,
    farms: farms,
    biomassTotal: biomassTotal,
    feedTotal: feedTotal,
    stockingTotal: stockingTotal,
  });

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" sx={{fontWeight: "bold"}}>
          {t("Dashboard")}
        </Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="body2" color="textSecondary">
            {t("Last updated")}: {lastUpdated.toLocaleTimeString()}
          </Typography>
          <MuiTooltip title={t("Refresh data")}>
            <span>
              <IconButton onClick={loadData} disabled={loading}>
                <Refresh />
              </IconButton>
            </span>
          </MuiTooltip>
        </Box>
      </Box>

      {/* Filters Section */}
      <Box sx={{mb: 3}}>
        {/* Compact Filter Bar */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 2,
            flexWrap: "wrap",
          }}>
          <Box display="flex" alignItems="center" gap={1}>
            <FilterList color="primary" />
            <Typography variant="h6">{t("Filters")}</Typography>
          </Box>

          {/* Active Filter Chips */}
          <Box display="flex" gap={1} flexWrap="wrap">
            {filters.selectedFarms.length > 0 && (
              <Chip
                label={`${filters.selectedFarms.length} ${t("Farms")}`}
                size="small"
                color="primary"
                onDelete={() => handleFilterChange("selectedFarms", [])}
              />
            )}
            {filters.selectedPonds.length > 0 && (
              <Chip
                label={`${filters.selectedPonds.length} ${t("Ponds")}`}
                size="small"
                color="primary"
                onDelete={() => handleFilterChange("selectedPonds", [])}
              />
            )}
            {filters.species.length > 0 && (
              <Chip
                label={`${filters.species.length} ${t("Species")}`}
                size="small"
                color="primary"
                onDelete={() => handleFilterChange("species", [])}
              />
            )}
            {filters.feedTypes.length > 0 && (
              <Chip
                label={`${filters.feedTypes.length} ${t("Feed Types")}`}
                size="small"
                color="primary"
                onDelete={() => handleFilterChange("feedTypes", [])}
              />
            )}
            {filters.pondTypes.length > 0 && (
              <Chip
                label={`${filters.pondTypes.length} ${t("Pond Types")}`}
                size="small"
                color="primary"
                onDelete={() => handleFilterChange("pondTypes", [])}
              />
            )}
            {filters.status !== "all" && (
              <Chip label={t(filters.status)} size="small" color="primary" onDelete={() => handleFilterChange("status", "all")} />
            )}
            {(filters.fcrRange[0] !== 1.0 || filters.fcrRange[1] !== 3.0) && (
              <Chip
                label={`FCR: ${filters.fcrRange[0]}-${filters.fcrRange[1]}`}
                size="small"
                color="primary"
                onDelete={() => handleFilterChange("fcrRange", [1.0, 3.0])}
              />
            )}
            {(filters.survivalRateRange[0] !== 70 || filters.survivalRateRange[1] !== 100) && (
              <Chip
                label={`Survival: ${filters.survivalRateRange[0]}-${filters.survivalRateRange[1]}%`}
                size="small"
                color="primary"
                onDelete={() => handleFilterChange("survivalRateRange", [70, 100])}
              />
            )}
            {(filters.waterQualityRange[0] !== 60 || filters.waterQualityRange[1] !== 100) && (
              <Chip
                label={`Water Quality: ${filters.waterQualityRange[0]}-${filters.waterQualityRange[1]}%`}
                size="small"
                color="primary"
                onDelete={() => handleFilterChange("waterQualityRange", [60, 100])}
              />
            )}
          </Box>

          {/* Action Buttons */}
          <Box display="flex" gap={1} ml="auto">
            <Button
              size="small"
              onClick={() => setFiltersExpanded(!filtersExpanded)}
              variant={filtersExpanded ? "contained" : "outlined"}
              startIcon={filtersExpanded ? <ExpandMore /> : <FilterList />}>
              {filtersExpanded ? t("Hide Filters") : t("Show Filters")}
            </Button>
            {Object.values(filters).some((filter) =>
              Array.isArray(filter)
                ? filter.length > 0
                : typeof filter === "object"
                ? Object.values(filter).some((v) => v !== "" && v !== "all")
                : filter !== "all"
            ) && (
              <Button size="small" startIcon={<Clear />} onClick={clearFilters} variant="outlined" color="secondary">
                {t("Clear All")}
              </Button>
            )}
          </Box>
        </Box>

        {/* Expanded Filter Panel */}
        <Collapse in={filtersExpanded}>
          <Paper sx={{p: 3, mb: 2}}>
            <Grid container spacing={3}>
              {/* Primary Filters Row */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom color="primary">
                  {t("Primary Filters")}
                </Typography>
              </Grid>

              {/* Farm Filter */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{t("Farms")}</InputLabel>
                  <Select
                    multiple
                    value={filters.selectedFarms}
                    onChange={(e) => handleFilterChange("selectedFarms", e.target.value)}
                    renderValue={(selected) => (
                      <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5}}>
                        {selected.map((value) => {
                          const farm = farms.find((f) => f.id === value);
                          return <Chip key={value} label={farm?.name || value} size="small" />;
                        })}
                      </Box>
                    )}>
                    {farms.map((farm) => (
                      <MenuItem key={farm.id} value={farm.id}>
                        {farm.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Pond Filter */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{t("Ponds")}</InputLabel>
                  <Select
                    multiple
                    value={filters.selectedPonds}
                    onChange={(e) => handleFilterChange("selectedPonds", e.target.value)}
                    renderValue={(selected) => (
                      <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5}}>
                        {selected.map((value) => {
                          const pond = ponds.find((p) => p.id === value);
                          const farm = farms.find((f) => f.id === pond?.farmId);
                          return <Chip key={value} label={`${pond?.name || value} - ${farm?.name || "Unknown Farm"}`} size="small" />;
                        })}
                      </Box>
                    )}>
                    {getAvailablePonds().map((pond) => {
                      const farm = farms.find((f) => f.id === pond.farmId);
                      return (
                        <MenuItem key={pond.id} value={pond.id}>
                          {pond.name} - {farm?.name || "Unknown Farm"} ({pond.type})
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>

              {/* Date Range */}
              <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={2}>
                  <TextField
                    label={t("Start Date")}
                    type="date"
                    value={filters.dateRange.start}
                    onChange={(e) =>
                      handleFilterChange("dateRange", {
                        ...filters.dateRange,
                        start: e.target.value,
                      })
                    }
                    fullWidth
                  />
                  <TextField
                    label={t("End Date")}
                    type="date"
                    value={filters.dateRange.end}
                    onChange={(e) =>
                      handleFilterChange("dateRange", {
                        ...filters.dateRange,
                        end: e.target.value,
                      })
                    }
                    fullWidth
                  />
                </Stack>
              </Grid>

              {/* Status Filter */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{t("Status")}</InputLabel>
                  <Select value={filters.status} onChange={(e) => handleFilterChange("status", e.target.value)}>
                    <MenuItem value="all">{t("All")}</MenuItem>
                    <MenuItem value="active">{t("Active")}</MenuItem>
                    <MenuItem value="inactive">{t("Inactive")}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Secondary Filters Row */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom color="primary" sx={{mt: 2}}>
                  {t("Category Filters")}
                </Typography>
              </Grid>

              {/* Species Filter */}
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>{t("Species")}</InputLabel>
                  <Select
                    multiple
                    value={filters.species}
                    onChange={(e) => handleFilterChange("species", e.target.value)}
                    renderValue={(selected) => (
                      <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5}}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}>
                    {speciesOptions.map((species) => (
                      <MenuItem key={species} value={species}>
                        {species}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Feed Type Filter */}
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>{t("Feed Types")}</InputLabel>
                  <Select
                    multiple
                    value={filters.feedTypes}
                    onChange={(e) => handleFilterChange("feedTypes", e.target.value)}
                    renderValue={(selected) => (
                      <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5}}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}>
                    {feedTypeOptions.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Pond Type Filter */}
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>{t("Pond Types")}</InputLabel>
                  <Select
                    multiple
                    value={filters.pondTypes}
                    onChange={(e) => handleFilterChange("pondTypes", e.target.value)}
                    renderValue={(selected) => (
                      <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5}}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}>
                    {pondTypeOptions.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Performance Metrics Filters */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom color="primary" sx={{mt: 2}}>
                  {t("Performance Metrics")}
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Box>
                      <Typography gutterBottom variant="body2">
                        {t("FCR Range")}: {filters.fcrRange[0]} - {filters.fcrRange[1]}
                      </Typography>
                      <Slider
                        value={filters.fcrRange}
                        onChange={(_, value) => handleFilterChange("fcrRange", value)}
                        valueLabelDisplay="auto"
                        min={0.5}
                        max={5.0}
                        step={0.1}
                        marks={[
                          {value: 0.5, label: "0.5"},
                          {value: 2.0, label: "2.0"},
                          {value: 5.0, label: "5.0"},
                        ]}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box>
                      <Typography gutterBottom variant="body2">
                        {t("Survival Rate")}: {filters.survivalRateRange[0]}% - {filters.survivalRateRange[1]}%
                      </Typography>
                      <Slider
                        value={filters.survivalRateRange}
                        onChange={(_, value) => handleFilterChange("survivalRateRange", value)}
                        valueLabelDisplay="auto"
                        min={0}
                        max={100}
                        step={5}
                        marks={[
                          {value: 0, label: "0%"},
                          {value: 50, label: "50%"},
                          {value: 100, label: "100%"},
                        ]}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box>
                      <Typography gutterBottom variant="body2">
                        {t("Water Quality Score")}: {filters.waterQualityRange[0]}% - {filters.waterQualityRange[1]}%
                      </Typography>
                      <Slider
                        value={filters.waterQualityRange}
                        onChange={(_, value) => handleFilterChange("waterQualityRange", value)}
                        valueLabelDisplay="auto"
                        min={0}
                        max={100}
                        step={5}
                        marks={[
                          {value: 0, label: "0%"},
                          {value: 50, label: "50%"},
                          {value: 100, label: "100%"},
                        ]}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Paper>
        </Collapse>
      </Box>

      {/* Alerts Section */}
      {alerts.length > 0 && (
        <Box mb={3}>
          {alerts.map((alert) => (
            <Alert
              key={alert.id}
              severity={alert.severity}
              sx={{mb: 1}}
              action={<Chip label={t("Dismiss")} size="small" onClick={() => handleDismissAlert(alert.id)} sx={{cursor: "pointer"}} />}>
              {alert.message}
            </Alert>
          ))}
        </Box>
      )}

      {/* Loading Indicator for Filters */}
      {filterLoading && (
        <Box sx={{mb: 2}}>
          <LinearProgress color="primary" />
          <Typography variant="body2" color="textSecondary" sx={{mt: 1}}>
            {t("Applying filters...")}
          </Typography>
        </Box>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t("Total Farms")}
            value={displayedFarmCount ?? farmCount ?? "..."}
            subtitle={t("Active operations")}
            color="#2196F3"
            isLoading={loading}
            trend={previousMonthData ? calculateTrend(farmCount || 0, previousMonthData.farms).direction : "up"}
            trendPercentage={previousMonthData ? calculateTrend(farmCount || 0, previousMonthData.farms).percentage : undefined}
            icon={<Visibility />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t("Total Ponds")}
            value={filteredStats?.totalPonds || pondCount || "..."}
            subtitle={t("Production units")}
            color="#4CAF50"
            isLoading={loading}
            trend={previousMonthData ? calculateTrend(pondCount || 0, previousMonthData.ponds).direction : "stable"}
            trendPercentage={previousMonthData ? calculateTrend(pondCount || 0, previousMonthData.ponds).percentage : undefined}
            icon={<Water />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t("Stocking")}
            value={stockingTotal !== null ? stockingTotal.toLocaleString() : "..."}
            subtitle={t("Total seed stocked")}
            color="#FF9800"
            isLoading={loading}
            trend={previousMonthData && stockingTotal ? calculateTrend(stockingTotal, previousMonthData.stocking).direction : "up"}
            trendPercentage={previousMonthData && stockingTotal ? calculateTrend(stockingTotal, previousMonthData.stocking).percentage : undefined}
            icon={<TrendingUp />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t("Biomass")}
            value={
              biomassTotal !== null && !isNaN(biomassTotal) && isFinite(biomassTotal) && biomassTotal > 0 ? `${Math.round(biomassTotal)} kg` : "N/A"
            }
            subtitle={t("Total production")}
            color="#9C27B0"
            isLoading={loading}
            trend={
              previousMonthData && biomassTotal && !isNaN(biomassTotal) && biomassTotal > 0
                ? calculateTrend(biomassTotal, previousMonthData.biomass).direction
                : undefined
            }
            trendPercentage={
              previousMonthData && biomassTotal && !isNaN(biomassTotal) && biomassTotal > 0
                ? calculateTrend(biomassTotal, previousMonthData.biomass).percentage
                : undefined
            }
            icon={<TrendingUp />}
          />
        </Grid>
      </Grid>

      {/* KPI Cards */}
      {dashboardStats && (
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6} md={3}>
            <KPICard
              title={t("Feed")}
              value={feedTotal !== null ? Math.round(feedTotal) : "N/A"}
              target={5000}
              unit="kg"
              color="#2196F3"
              isLoading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KPICard
              title={t("Feed Efficiency")}
              value={(() => {
                const fcr = dashboardStats.feedEfficiency;
                if (fcr === undefined || fcr === null || isNaN(fcr)) {
                  console.warn("Invalid FCR value:", fcr);
                  return 1.15; // Fallback value
                }
                return Number(Number(fcr).toFixed(2));
              })()}
              target={1.3}
              unit=" FCR"
              color="#4CAF50"
              isLoading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KPICard
              title={t("Survival Rate")}
              value={filteredStats?.survivalRate ? Math.round(filteredStats.survivalRate) : "N/A"}
              target={85}
              unit="%"
              color="#FF9800"
              subtitle={filteredStats?.totalSurvivalNumber ? `(${filteredStats.totalSurvivalNumber.toLocaleString()} nos)` : ""}
              isLoading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KPICard
              title={t("Water Quality Score")}
              value={waterQualityScoreValue}
              target={90}
              unit="%"
              color="#9C27B0"
              isLoading={loading}
            />
          </Grid>
        </Grid>
      )}

      {/* Market Prices Widget */}
      <Box sx={{mt: 4}}>
        <Typography variant="h5" gutterBottom sx={{fontWeight: "bold", mb: 2}}>
          Market Prices
        </Typography>
        <Card>
          <CardContent>
            <Grid container spacing={2} sx={{mb: 2}}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Select Area</InputLabel>
                  <Select label="Select Area" value={selectedMarketAreaId} onChange={(e) => setSelectedMarketAreaId(e.target.value)}>
                    {activeMarketAreas.map((area) => (
                      <MenuItem key={area.id} value={area.id}>
                        {area.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Commodity</InputLabel>
                  <Select
                    label="Commodity"
                    value={selectedMarketCommodityId}
                    onChange={(e) => setSelectedMarketCommodityId(e.target.value)}>
                    {activeMarketCommodities.map((commodity) => (
                      <MenuItem key={commodity.id} value={commodity.id}>
                        {commodity.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {marketError && (
              <Alert severity="error" sx={{mb: 2}}>
                {marketError}
              </Alert>
            )}

            <Box sx={{overflowX: "auto"}}>
              <table style={{width: "100%", borderCollapse: "collapse"}}>
                <thead>
                  <tr style={{borderBottom: "2px solid #e0e0e0"}}>
                    <th style={{padding: "12px", textAlign: "left", fontWeight: "bold", fontSize: "0.875rem"}}>Area</th>
                    <th style={{padding: "12px", textAlign: "left", fontWeight: "bold", fontSize: "0.875rem"}}>Commodity</th>
                    <th style={{padding: "12px", textAlign: "left", fontWeight: "bold", fontSize: "0.875rem"}}>Type</th>
                    <th style={{padding: "12px", textAlign: "left", fontWeight: "bold", fontSize: "0.875rem"}}>Price (₹/kg)</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedMarketRows.length === 0 && (
                    <tr>
                      <td style={{padding: "12px"}} colSpan={4}>
                        {marketLoading ? (
                          <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", py: 2}}>
                            <CircularProgress size={22} />
                          </Box>
                        ) : (
                          "No market prices configured"
                        )}
                      </td>
                    </tr>
                  )}
                  {sortedMarketRows.map((row) => (
                    <tr key={row.id} style={{borderBottom: "1px solid #f0f0f0"}}>
                      <td style={{padding: "12px", fontSize: "0.8rem"}}>{row.area?.name || "-"}</td>
                      <td style={{padding: "12px", fontSize: "0.8rem"}}>{row.commodity?.name || "-"}</td>
                      <td style={{padding: "12px", fontSize: "0.8rem"}}>
                        {row.pricingMode === "COUNT_WISE" ? `${row.count || "-"}C ${row.label ? `(${row.label})` : ""}` : row.label || "PER_KG"}
                      </td>
                      <td style={{padding: "12px", fontSize: "0.8rem", fontWeight: 600}}>₹ {Math.round(Number(row.pricePerKg))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Active Pond List */}
      <Box sx={{mt: 4}}>
        <Typography variant="h5" gutterBottom sx={{fontWeight: "bold", mb: 3}}>
          {t("Active Pond List")}
        </Typography>
        <Card>
          <CardContent>
            <Box sx={{overflowX: "auto"}}>
              <table style={{width: "100%", borderCollapse: "collapse"}}>
                <thead>
                  <tr style={{borderBottom: "2px solid #e0e0e0"}}>
                    <th style={{padding: "12px", textAlign: "left", fontWeight: "bold", fontSize: "0.875rem"}}>{t("Pond")}</th>
                    <th style={{padding: "12px", textAlign: "left", fontWeight: "bold", fontSize: "0.875rem"}}>{t("Farm")}</th>
                    <th style={{padding: "12px", textAlign: "left", fontWeight: "bold", fontSize: "0.875rem"}}>{t("Age (Days)")}</th>
                    <th style={{padding: "12px", textAlign: "left", fontWeight: "bold", fontSize: "0.875rem"}}>{t("Stocking Date")}</th>
                    <th style={{padding: "12px", textAlign: "left", fontWeight: "bold", fontSize: "0.875rem"}}>{t("Stocking Number")}</th>
                    <th style={{padding: "12px", textAlign: "left", fontWeight: "bold", fontSize: "0.875rem"}}>{t("Biomass (kg)")}</th>
                    <th style={{padding: "12px", textAlign: "left", fontWeight: "bold", fontSize: "0.875rem"}}>{t("Feed (kg)")}</th>
                    <th style={{padding: "12px", textAlign: "left", fontWeight: "bold", fontSize: "0.875rem"}}>{t("Count")}</th>
                    <th style={{padding: "12px", textAlign: "left", fontWeight: "bold", fontSize: "0.875rem"}}>{t("FCR")}</th>
                    <th style={{padding: "12px", textAlign: "left", fontWeight: "bold", fontSize: "0.875rem"}}>{t("SR (%)")}</th>
                    <th style={{padding: "12px", textAlign: "left", fontWeight: "bold", fontSize: "0.875rem"}}>{t("Status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {getFilteredPonds()
                    .filter((pond) => pond.isActive)
                    .map((pond, index) => {
                      const farm = farms.find((f) => f.id === pond.farmId);
                      const pondPerformance = pond.performanceMetrics;

                      // Get current cycle data from performance metrics
                      const currentCycle = pondPerformance?.currentCycle;

                      // Calculate real age in days from cycle start date
                      let ageInDays = 0;
                      let stockingDate = new Date();
                      if (currentCycle?.startedAt) {
                        stockingDate = new Date(currentCycle.startedAt);
                        const today = new Date();
                        ageInDays = Math.floor((today.getTime() - stockingDate.getTime()) / (1000 * 60 * 60 * 24));
                      }

                      // Get real stocking number from current cycle
                      const stockingNumber = currentCycle?.totalSeed || 0;

                      // Get real feed consumption from performance metrics
                      const totalFeed = pondPerformance?.totalFeed || 0;

                      // Get ABW from performance metrics
                      const abw = pondPerformance?.abw || 0;

                      // Calculate count as 1000/ABW (shrimp per kg)
                      const count = abw > 0 ? Math.round(1000 / abw) : 0;

                      // Get real FCR from performance metrics
                      const fcr = Number(pondPerformance?.fcr) || 0;

                      // Calculate real survival rate
                      let survivalRate = 0;
                      if (pondPerformance?.sr) {
                        survivalRate = Math.round(pondPerformance.sr);
                      } else if (pondPerformance?.calculatedSurvival && stockingNumber > 0) {
                        survivalRate = Math.round((pondPerformance.calculatedSurvival / stockingNumber) * 100);
                      }

                      // Calculate real biomass range
                      let biomassRange = "N/A";
                      if (pondPerformance?.biomassRange) {
                        const min = Number(pondPerformance.biomassRange.min) || 0;
                        const max = Number(pondPerformance.biomassRange.max) || 0;
                        biomassRange = `${Math.round(min)}-${Math.round(max)}`;
                      } else if (pondPerformance?.calculatedBiomass) {
                        const biomass = Number(pondPerformance.calculatedBiomass) || 0;
                        if (biomass > 0) {
                          const range = biomass * 0.1; // ±10% range
                          const min = Math.max(0, biomass - range);
                          const max = biomass + range;
                          biomassRange = `${Math.round(min)}-${Math.round(max)}`;
                        }
                      }

                      // Debug logging for real data usage
                      if (process.env.NODE_ENV === "development") {
                        console.log(`📊 Pond ${pond.name} Real Data:`, {
                          stockingNumber,
                          totalFeed,
                          count,
                          fcr,
                          survivalRate,
                          biomassRange,
                          hasPerformanceMetrics: !!pondPerformance,
                          hasCurrentCycle: !!currentCycle,
                        });
                      }

                      return (
                        <tr key={pond.id} style={{borderBottom: "1px solid #f0f0f0"}}>
                          <td style={{padding: "12px", fontSize: "0.8rem"}}>
                            <Button
                              component={Link}
                              to={`/ponds/${pond.id}`}
                              sx={{textTransform: "none", fontWeight: "bold", p: 0, minWidth: "auto"}}>
                              {pond.name}
                            </Button>
                          </td>
                          <td style={{padding: "12px", fontSize: "0.8rem"}}>{farm?.name || "N/A"}</td>
                          <td style={{padding: "12px", fontSize: "0.8rem"}}>{ageInDays}</td>
                          <td style={{padding: "12px", fontSize: "0.8rem"}}>{stockingDate.toLocaleDateString()}</td>
                          <td style={{padding: "12px", fontSize: "0.8rem"}}>{stockingNumber.toLocaleString()}</td>
                          <td style={{padding: "12px", fontSize: "0.8rem"}}>{biomassRange}</td>
                          <td style={{padding: "12px", fontSize: "0.8rem"}}>{Math.round(totalFeed).toLocaleString()}</td>
                          <td style={{padding: "12px", fontSize: "0.8rem"}}>{count.toLocaleString()}</td>
                          <td style={{padding: "12px", fontSize: "0.8rem"}}>{fcr > 0 ? fcr.toFixed(2) : "N/A"}</td>
                          <td style={{padding: "12px", fontSize: "0.8rem"}}>{survivalRate > 0 ? `${survivalRate}%` : "N/A"}</td>
                          <td style={{padding: "12px", fontSize: "0.8rem"}}>
                            <Chip label={pond.isActive ? t("Active") : t("Inactive")} size="small" color={pond.isActive ? "success" : "default"} />
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </Box>
            {getFilteredPonds().filter((pond) => pond.isActive).length === 0 && (
              <Box sx={{textAlign: "center", py: 4}}>
                {loading ? (
                  <CircularProgress size={24} />
                ) : (
                  <Typography variant="body1" color="textSecondary">
                    {t("No active ponds found")}
                  </Typography>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Today's Feed Table */}
      <Box sx={{mt: 4}}>
        <Typography variant="h5" gutterBottom sx={{fontWeight: "bold", mb: 3}}>
          {t("Today's Feed")} (<span dangerouslySetInnerHTML={{__html: formatShortDate(new Date())}} />)
        </Typography>
        <Card>
          <CardContent>
            <Box sx={{overflowX: "auto"}}>
              <table style={{width: "100%", borderCollapse: "collapse"}}>
                <thead>
                  <tr style={{backgroundColor: "primary.main", color: "primary.contrastText", borderBottom: "2px solid #e0e0e0"}}>
                    <th style={{padding: "12px", textAlign: "left", fontWeight: "bold", fontSize: "0.875rem"}}>{t("Pond")}</th>
                    <th style={{padding: "12px", textAlign: "right", fontWeight: "bold", fontSize: "0.875rem"}}>{t("Age")}</th>
                    <th style={{padding: "12px", textAlign: "right", fontWeight: "bold", fontSize: "0.875rem"}}>{t("Feed1")}</th>
                    <th style={{padding: "12px", textAlign: "right", fontWeight: "bold", fontSize: "0.875rem"}}>{t("Feed2")}</th>
                    <th style={{padding: "12px", textAlign: "right", fontWeight: "bold", fontSize: "0.875rem"}}>{t("Feed3")}</th>
                    <th style={{padding: "12px", textAlign: "right", fontWeight: "bold", fontSize: "0.875rem"}}>{t("Feed4")}</th>
                    <th style={{padding: "12px", textAlign: "right", fontWeight: "bold", fontSize: "0.875rem"}}>{t("Today's Total Feed")}</th>
                  </tr>
                </thead>
                <tbody>
                  {getFilteredPonds()
                    .filter((pond) => pond.isActive)
                    .map((pond, index) => {
                      const farm = farms.find((f) => f.id === pond.farmId);
                      const pondPerformance = pond.performanceMetrics;

                      // Get current cycle data from performance metrics
                      const currentCycle = pondPerformance?.currentCycle;

                      // Calculate real age in days from cycle start date
                      let ageInDays = 0;
                      if (currentCycle?.startedAt) {
                        const stockingDate = new Date(currentCycle.startedAt);
                        const today = new Date();
                        ageInDays = Math.floor((today.getTime() - stockingDate.getTime()) / (1000 * 60 * 60 * 24));
                      }

                      // Get today's feed data for this pond
                      const todayFeedRecord = Array.isArray(todayFeedRecords) ? todayFeedRecords.find((record) => record.pondId === pond.id) : null;

                      let feed1 = 0,
                        feed2 = 0,
                        feed3 = 0,
                        feed4 = 0,
                        totalFeed = 0;

                      if (todayFeedRecord) {
                        feed1 = parseFloat(todayFeedRecord.feed1?.toString() || "0");
                        feed2 = parseFloat(todayFeedRecord.feed2?.toString() || "0");
                        feed3 = parseFloat(todayFeedRecord.feed3?.toString() || "0");
                        feed4 = parseFloat(todayFeedRecord.feed4?.toString() || "0");
                        totalFeed = parseFloat(todayFeedRecord.totalQuantity?.toString() || "0");
                      }

                      return (
                        <tr key={pond.id} style={{borderBottom: "1px solid #f0f0f0"}}>
                          <td style={{padding: "12px", fontSize: "0.8rem"}}>
                            <Button
                              component={Link}
                              to={`/ponds/${pond.id}`}
                              sx={{textTransform: "none", fontWeight: "bold", p: 0, minWidth: "auto"}}>
                              {pond.name}
                            </Button>
                          </td>
                          <td style={{padding: "12px", fontSize: "0.8rem", textAlign: "right"}}>{ageInDays}</td>
                          <td style={{padding: "12px", fontSize: "0.8rem", textAlign: "right"}}>{feed1}</td>
                          <td style={{padding: "12px", fontSize: "0.8rem", textAlign: "right"}}>{feed2}</td>
                          <td style={{padding: "12px", fontSize: "0.8rem", textAlign: "right"}}>{feed3}</td>
                          <td style={{padding: "12px", fontSize: "0.8rem", textAlign: "right"}}>{feed4}</td>
                          <td style={{padding: "12px", fontSize: "0.8rem", textAlign: "right", fontWeight: "bold"}}>{totalFeed}</td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </Box>
            {getFilteredPonds().filter((pond) => pond.isActive).length === 0 && (
              <Box sx={{textAlign: "center", py: 4}}>
                {loading ? (
                  <CircularProgress size={24} />
                ) : (
                  <Typography variant="body1" color="textSecondary">
                    {t("No active ponds found")}
                  </Typography>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Charts Grid */}
      {(chartData || getTranslatedChartData(t)) && (
        <Grid container spacing={3}>
          {/* Biomass Growth Tracking Chart - Daily Data */}
          <Grid item xs={12}>
            <Card>
              <CardHeader title={t("Biomass Growth Tracking")} subheader={t("Daily biomass production by species (kg)")} />
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart
                    data={(() => {
                      const chartDataToUse = getFilteredChartData()?.biomassData || chartData?.biomassData || getTranslatedChartData(t).biomassData;
                      console.log("📈 Biomass Chart Data:", {
                        data: chartDataToUse,
                        length: chartDataToUse?.length,
                        firstPoint: chartDataToUse?.[0],
                        lastPoint: chartDataToUse?.[chartDataToUse?.length - 1],
                      });
                      return chartDataToUse;
                    })()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <RechartsTooltip />
                    <Legend />
                    <Area type="monotone" dataKey="tilapia" stackId="1" stroke="#8884d8" fill="#8884d8" />
                    <Area type="monotone" dataKey="catfish" stackId="1" stroke="#82ca9d" fill="#82ca9d" />
                    <Area type="monotone" dataKey="shrimp" stackId="1" stroke="#ffc658" fill="#ffc658" />
                  </AreaChart>
                </ResponsiveContainer>
                {/* Debug info */}
                {process.env.NODE_ENV === "development" && (
                  <Box sx={{mt: 1, p: 1, bgcolor: "grey.100", fontSize: "0.8rem"}}>
                    <Typography variant="caption">
                      Debug: Biomass data points: {getFilteredChartData()?.biomassData?.length || chartData?.biomassData?.length || 0}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Feed Consumption Trends Chart - Daily Data */}
          <Grid item xs={12}>
            <Card>
              <CardHeader title={t("Feed Consumption Trends")} subheader={t("Daily feed usage by type (kg)")} />
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={(() => {
                      const chartDataToUse =
                        getFilteredChartData()?.feedConsumptionData ||
                        chartData?.feedConsumptionData ||
                        getTranslatedChartData(t).feedConsumptionData;
                      console.log("📊 Feed Consumption Chart Data:", {
                        data: chartDataToUse,
                        length: chartDataToUse?.length,
                        firstPoint: chartDataToUse?.[0],
                        lastPoint: chartDataToUse?.[chartDataToUse?.length - 1],
                      });
                      return chartDataToUse;
                    })()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="week" />
                    <YAxis />
                    <RechartsTooltip />
                    <Legend />
                    <Bar dataKey="starter" fill="#8884d8" />
                    <Bar dataKey="grower" fill="#82ca9d" />
                    <Bar dataKey="finisher" fill="#ffc658" />
                  </BarChart>
                </ResponsiveContainer>
                {/* Debug info */}
                {process.env.NODE_ENV === "development" && (
                  <Box sx={{mt: 1, p: 1, bgcolor: "grey.100", fontSize: "0.8rem"}}>
                    <Typography variant="caption">
                      Debug: Feed consumption data points:{" "}
                      {getFilteredChartData()?.feedConsumptionData?.length || chartData?.feedConsumptionData?.length || 0}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Water Quality Monitoring */}
          <Grid item xs={12}>
            <Card>
              <CardHeader title={t("Water Quality Monitoring")} subheader={t("Key parameters over time")} />
              <CardContent>
                {hasRealWaterQualityData ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={waterQualitySeries}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" />
                      <RechartsTooltip />
                      <Legend />
                      <Line yAxisId="left" type="monotone" dataKey="temperature" stroke="#8884d8" name={t("Temperature (°C)")} />
                      <Line yAxisId="left" type="monotone" dataKey="ph" stroke="#82ca9d" name="pH" />
                      <Line yAxisId="right" type="monotone" dataKey="dissolvedOxygen" stroke="#ffc658" name={t("DO (mg/L)")} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{height: 300, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 1}}>
                    <Typography variant="h4" sx={{fontWeight: "bold"}}>
                      N/A
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {t("Data not available")}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default DashboardPage;
